import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import {
  Menu,
  LogOut,
  Heart,
  History,
  BookOpen,
  ShieldCheck,
  Settings,
  HelpCircle,
  BarChart3,
  Headphones,
  Home,
  LayoutGrid,
  UserCircle,
} from "lucide-react";

// Hamburger menu shown in the top-right of every signed-in page.
//
// Previous version used Radix DropdownMenu which became unresponsive on
// iPadOS 26.5 (App Review flagged this — pointer events from Radix don't
// always register on the new iPadOS pointer model). This is a hand-rolled
// dropdown that uses plain onClick + onTouchStart for guaranteed touch
// reliability on iPad and tracks outside-clicks / Escape via refs.
//
// Keeps navigation discoverable on routes that don't have full nav bars
// (Library, History, Favorites, Settings, Disclaimer, Intervention,
// Getting Started, Admin). Dashboard uses its own richer version with
// live counts; this is the lightweight version for everywhere else.

const SECTIONS = [
  { label: "Dashboard", icon: Home, path: "/dashboard", testid: "menu-dashboard" },
  { label: "Getting Started", icon: BookOpen, path: "/getting-started", testid: "menu-getting-started" },
  { divider: true },
  { label: "Practice Library", icon: LayoutGrid, path: "/practices", testid: "menu-practices" },
  { label: "Library", icon: Headphones, path: "/library", testid: "menu-library" },
  { label: "History", icon: History, path: "/history", testid: "menu-history" },
  { label: "Favorites", icon: Heart, path: "/favorites", testid: "menu-favorites" },
  { divider: true },
  { label: "Account", icon: UserCircle, path: "/account", testid: "menu-account" },
  { label: "Settings", icon: Settings, path: "/settings", testid: "menu-settings" },
  { label: "FAQ", icon: HelpCircle, path: "/faq", testid: "menu-faq" },
  { label: "Disclaimer", icon: ShieldCheck, path: "/disclaimer", testid: "menu-disclaimer" },
];

export const AppMenu = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Close on outside tap. Listen to both mouse + touch so iPad behaves.
  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handle);
    document.addEventListener("touchstart", handle);
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", handle);
      document.removeEventListener("touchstart", handle);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const go = useCallback((path) => {
    setOpen(false);
    // setTimeout 0 avoids React reconciler conflicts on rapid touch events
    setTimeout(() => navigate(path), 0);
  }, [navigate]);

  const handleLogout = useCallback(() => {
    setOpen(false);
    setTimeout(() => logout(), 0);
  }, [logout]);

  return (
    <div ref={containerRef} className="relative" style={{ touchAction: "manipulation" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-10 h-10 rounded-full hover:bg-slate-100 active:bg-slate-200 flex items-center justify-center transition-colors"
        style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
        data-testid="user-menu-btn"
        aria-label="Open menu"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <Menu className="w-5 h-5 text-[#1B2349]" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-56 rounded-xl bg-white shadow-lg ring-1 ring-black/5 overflow-hidden z-50 py-1"
          data-testid="user-menu-content"
        >
          {user && (
            <div className="px-3 py-2 border-b border-slate-100">
              <p className="text-sm font-medium text-[#1B2349] truncate">{user.name}</p>
              <p className="text-xs text-[#718096] truncate">{user.email}</p>
            </div>
          )}
          {SECTIONS.map((item, idx) => {
            if (item.divider) {
              return <div key={`d${idx}`} className="my-1 border-t border-slate-100" />;
            }
            const Icon = item.icon;
            return (
              <button
                key={item.path}
                type="button"
                onClick={() => go(item.path)}
                role="menuitem"
                className="w-full px-3 py-2 text-left text-sm text-[#1B2349] hover:bg-slate-50 active:bg-slate-100 flex items-center gap-2"
                style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
                data-testid={item.testid}
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </button>
            );
          })}
          {user?.role === "admin" && (
            <button
              type="button"
              onClick={() => go("/admin")}
              role="menuitem"
              className="w-full px-3 py-2 text-left text-sm text-[#1B2349] hover:bg-slate-50 active:bg-slate-100 flex items-center gap-2"
              style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
              data-testid="menu-analytics"
            >
              <BarChart3 className="w-4 h-4" />
              Analytics
            </button>
          )}
          <div className="my-1 border-t border-slate-100" />
          <button
            type="button"
            onClick={handleLogout}
            role="menuitem"
            className="w-full px-3 py-2 text-left text-sm text-[#D98880] hover:bg-slate-50 active:bg-slate-100 flex items-center gap-2"
            style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
            data-testid="menu-logout"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

export default AppMenu;
